/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2025, Bulb Inc. For more information, please email contact@hellobulb.com.
 *          _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
*/

import {
  Component, Inject, Input, OnInit,
} from '@angular/core';
import { finalize } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { TranslationService } from '../../../common/translation.service';
import { BULB_BUSY_COVER_SERVICE } from '../../angularjs.providers';
import { ButtonSize, ButtonStyle } from '../../../aero/aero-button/aero-button.component';
import { AeroDialogService } from '../../../aero/aero-dialog/aero-dialog.service';

/**
 * Component that loads when Org admin clicks copy button.
 */
@Component({
  selector: 'bulb-tags',
  templateUrl: './tags.component.html',
  styleUrls: ['./tags.component.scss'],
  standalone: false,
})
export class TagsComponent implements OnInit {

  @Input() organizationId: string;

  constructor(
    private $translate: TranslationService,
    private httpClient: HttpClient,
    private aeroDialogService: AeroDialogService,
    @Inject(BULB_BUSY_COVER_SERVICE) private bulbBusyCoverService: any,
    @Inject('BulbErrorService') private bulbErrorService,
  ) {
  }

  gradeMapping = {
    PRESCHOOL: this.$translate.translateInstant('organization_dashboard_preschool_letter'),
    KINDERGARTEN: this.$translate.translateInstant('organization_dashboard_kindergarten_letter'),
    FIRST: '1',
    SECOND: '2',
    THIRD: '3',
    FOURTH: '4',
    FIFTH: '5',
    SIXTH: '6',
    SEVENTH: '7',
    EIGHTH: '8',
    NINTH: '9',
    TENTH: '10',
    ELEVENTH: '11',
    TWELFTH: '12',
    POSTGRADUATE: this.$translate.translateInstant('grade_level_POSTGRADUATE'),
    OTHER: this.$translate.translateInstant('organization_dashboard_other_letters'),
    UNASSIGNED: this.$translate.translateInstant('grade_level_UNASSIGNED'),
  };

  buttonSlimSize: ButtonSize = ButtonSize.SLIM;

  gradeOptions = [
    { code: 'PRESCHOOL', name: this.$translate.translateInstant('organization_dashboard_preschool_letter') },
    {
      code: 'KINDERGARTEN',
      name: this.$translate.translateInstant('organization_dashboard_kindergarten_letter'),
    },
    { code: 'FIRST', name: '1' }, { code: 'SECOND', name: '2' },
    { code: 'THIRD', name: '3' }, { code: 'FOURTH', name: '4' },
    { code: 'FIFTH', name: '5' }, { code: 'SIXTH', name: '6' },
    { code: 'SEVENTH', name: '7' }, { code: 'EIGHTH', name: '8' },
    { code: 'NINTH', name: '9' }, { code: 'TENTH', name: '10' },
    { code: 'ELEVENTH', name: '11' }, { code: 'TWELFTH', name: '12' },
    { code: 'POSTGRADUATE', name: this.$translate.translateInstant('grade_level_POSTGRADUATE') },
    { code: 'OTHER', name: this.$translate.translateInstant('organization_dashboard_other_letters') },
    { code: 'UNASSIGNED', name: this.$translate.translateInstant('grade_level_UNASSIGNED') },
  ];

  activeTags = [];

  inactiveTags = [];

  newTag = this.resetNewTag();

  loadTags() {
    const stopBusyIndicatorCallback = this.bulbBusyCoverService.showBusy();
    this.httpClient.get(
      `tags/organization/${this.organizationId}`,
    ).pipe(
      finalize(() => {
        stopBusyIndicatorCallback();
      }),
    ).subscribe(
      {
        next: (tags: any[]) => {
          this.activeTags = tags.filter((tag) => tag.active);
          this.inactiveTags = tags.filter((tag) => !tag.active);
        },
        error: (err) => {
          this.bulbErrorService.showError(null, err);
        },
      },
    );
  }

  ngOnInit(): void {
    this.loadTags();
  }

  resetNewTag() {
    return {
      id: '',
      name: '',
      description: '',
      colorCode: '#00AEEF',
      // expectedUses: null,
      grades: [],
    };
  }

  formatGrades(grades) {
    if (!grades || !grades.length) return '';

    return grades
      .map((g) => this.gradeMapping[g] || g)
      .join(', ');
  }

  toggleAddForm() {
    if (this.activeTags.length < 20) {
      const dialog = this.aeroDialogService.showTagsDialog(this.organizationId);

      const subscription = dialog.onClose.subscribe((createdTag) => {
        if (createdTag) {
          this.activeTags.push(angular.copy(createdTag));
        }
        this.aeroDialogService.removeDialog();
        subscription.unsubscribe();
      });
    }
  }

  editTag(tag, index: number) {
    const dialog = this.aeroDialogService.showTagsDialog(this.organizationId, tag);

    const subscription = dialog.onClose.subscribe((updatedTag) => {
      if (updatedTag) {
        // ✅ replace at same index
        this.activeTags[index] = angular.copy(updatedTag);
      }

      this.aeroDialogService.removeDialog();
      subscription.unsubscribe();
    });
  }

  /**
   * Show Confirm dialog.
   */
  showConfirmDialog(tag) {
    this.aeroDialogService.showDialog({
      icon: 'far fa-trash-alt',
      iconClass: 'aero-alert',
      title: this.$translate.translateInstant('organization_turn_off_integration_modal_title'),
      detail: 'Do you really want to delete this tag?',
      primaryLabel: this.$translate.translateInstant('button_delete'),
      primaryStyle: ButtonStyle.NEGATIVE_ACTION,
      onPrimary: () => {
        this.deleteTag(tag);
      },
    });
  }

  deleteTag(tag) {
    const index = this.activeTags.indexOf(tag);
    if (index === -1) {
      return;
    }

    const stopBusyIndicatorCallback = this.bulbBusyCoverService.showBusy();

    this.httpClient.delete(`tags/organization/${this.organizationId}/${tag.id}`)
      .pipe(
        finalize(() => {
          stopBusyIndicatorCallback();
        }),
      )
      .subscribe({
        next: () => {
          this.activeTags.splice(index, 1);
          this.inactiveTags.push(tag);
        },
        error: (error) => {
          this.bulbErrorService.showError(null, error);
        },
      });
  }
}
